import React from 'react'

import { EDataMode } from 'types/Enums'
import { SxProps } from '@mui/material'

import { useLanguage, useTranslation } from 'language/translator'
import { useCustomer } from 'redux/hooks/Customer'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import Skeleton from 'components_new/atoms/Skeleton'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import { getParsedDate } from 'utils/dateParser'

interface DataStatusProps {
  align?: 'left' | 'right'
  date?: string | null // controlled
  mode?: EDataMode
  variant?: 'chip' | 'item'
}

const DataStatus = (props: DataStatusProps) => {
  const { align = 'left', date, mode = EDataMode.SYNCED, variant } = props

  const customer = useCustomer()
  const { lang } = useLanguage()
  const { t } = useTranslation()

  const loading = !customer

  const details =
    mode === EDataMode.LOCKED && date
      ? getParsedDate(date, lang)
      : mode === EDataMode.SYNCED
        ? getParsedDate(customer?.latest_job_run, lang, { time: 'full' })
        : null

  if (variant === 'chip')
    return (
      <Tooltip title={details}>
        <Chip
          icon={
            mode === EDataMode.LOCKED ? (
              <Icon fontSize="small" name="Lock" />
            ) : (
              <SyncIcon sx={{ ml: 0.75, mr: -0.25 }} />
            )
          }
          label={t(
            'global',
            mode === EDataMode.LOCKED ? 'data_locked' : 'data_updated'
          )}
          size="small"
          sx={{ cursor: 'default' }}
        />
      </Tooltip>
    )

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
      {mode === EDataMode.LOCKED ? (
        <Icon fontSize="small" name="Lock" />
      ) : (
        <SyncIcon />
      )}
      <Box>
        <Text
          align={align}
          color="text.primary"
          component="div"
          fontWeight="medium"
          noWrap={true}
          variant="body2"
        >
          {t(
            'global',
            mode === EDataMode.LOCKED ? 'data_locked' : 'data_updated'
          )}
        </Text>
        <Text
          align={align}
          color="text.secondary"
          component="div"
          noWrap={true}
          variant="body2"
        >
          {loading ? <Skeleton variant="text" /> : details}
        </Text>
      </Box>
    </Box>
  )
}

export default DataStatus

const SyncIcon = (props: { sx?: SxProps }) => {
  return (
    <Box
      component="span"
      sx={{
        '@keyframes pulse': {
          '0%, 100%': {
            boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.4)',
            transform: 'scale(1)'
          },
          '50%': {
            boxShadow: '0 0 0 4px rgba(76, 175, 80, 0)',
            transform: 'scale(1.1)'
          }
        },
        animation: 'pulse 5s ease-in-out infinite',
        bgcolor: 'success.main',
        borderRadius: '50%',
        flexShrink: 0,
        height: 8,
        width: 8,
        ...props.sx
      }}
    />
  )
}
